/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/**
 * Effective-stage resolver — a pure function answering exactly one question:
 * which lifecycle stage should the framework evaluate this feature at?
 *
 * It sits between the registry and `resolveStageVisibility`:
 *
 *   registry stage ──► resolveEffectiveStage(registryStage, overrideStage)
 *                              │
 *                              ▼
 *                        effective stage ──► resolveStageVisibility(…)
 *
 * The registry is never modified: an override is a read-time substitution only,
 * supplied by a dev-tools-only `IFeatureStageOverrideProvider`. In a release
 * build no provider is injected, so `overrideStage` is always `null` and this
 * returns the registry stage unchanged.
 *
 * Kept separate from `FeatureActivation` so the debug screen can display the
 * same "registry stage vs effective stage" pair the engine actually resolves,
 * without duplicating the rule.
 */

import type { FeatureStage } from "./types";

export function resolveEffectiveStage(
  registryStage: FeatureStage,
  overrideStage: FeatureStage | null
): FeatureStage {
  return overrideStage === null ? registryStage : overrideStage;
}
